import { FC, ReactNode } from 'react';

import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupPageControls } from 'features/common/components/Punchup/primitives/layout/PunchupPageControls';
import { PunchupTableLayout } from 'features/common/components/Punchup/primitives/table/PunchupTableLayout';

export interface PunchupTablePageProps {
	/**
	 * Sub-page name — forwarded to `PunchupPageShell` (drives the breadcrumb +
	 * `sub-page-enter`). Must match the `title` on the `header` node.
	 */
	pageName: string;
	/**
	 * Optional back-navigation override, forwarded to `PunchupPageShell` (the
	 * standalone-mode Back button + the `navigate-to-dashboard` handler).
	 */
	onBack?: () => void;
	/**
	 * The page header. Compose `<PunchupTablePageHeader title description
	 * [customAction] [onDownload…] [onRefresh…] />` and pass it here. Kept as a
	 * slot rather than re-exposing the header's props so this component never
	 * grows a parallel header API — see `CLAUDE.md` (the no-flags rule).
	 */
	header: ReactNode;
	/**
	 * `PunchupPageControls` left slot — the search input (always a
	 * `PunchupSearch`). Pass `null` to keep the controls row but hide the search
	 * box (a grouped view that filters nothing searchable). Omit entirely only
	 * when the page has no controls row at all.
	 */
	search?: ReactNode;
	/**
	 * `PunchupPageControls` right slot — filter selects, column pickers, or a
	 * bespoke controls cluster.
	 */
	controlsActions?: ReactNode;
	/**
	 * The feature table: a component returning a `PunchupTableContainer` (and its
	 * `PunchupLoadMore`). Rendered inside `PunchupTableLayout` beside the controls
	 * row so their widths align. The table body stays feature-owned — this
	 * component never iterates rows.
	 */
	children: ReactNode;
	/**
	 * When provided, REPLACES the entire page body (header + controls + table)
	 * inside the shell — for a whole-page empty/fallback state (e.g.
	 * `NoEventsFallback`). The caller decides by passing or omitting the node;
	 * there is deliberately no boolean predicate prop.
	 */
	pageFallback?: ReactNode;
	/**
	 * Page-level overlays (confirmation modals, dialogs) rendered as a sibling of
	 * the page body, inside the shell — where pages like Comped Tickets and
	 * Lottery Entries mount their action modals.
	 */
	overlays?: ReactNode;
}

/**
 * The shared assembly for every Punchup **table** sub-page. Owns the fixed
 * vertical skeleton —
 * `PunchupPageShell → PunchupPageContent → [header] → PunchupTableLayout →
 * [PunchupPageControls, table]` — so feature pages stop re-wiring it by hand and
 * the canonical structure lives in exactly one place.
 *
 * It is a PURE slot assembly: zero data fetching, zero column logic, zero error
 * policy, and — critically — **no boolean behaviour flags**. Every per-page
 * difference is expressed by passing or omitting a `ReactNode` slot (`header`,
 * `search`, `controlsActions`, `children`, `pageFallback`, `overlays`). That is
 * what keeps it a generic cross-feature shell eligible for `pages/` rather than
 * a feature page or a prop-explosion config monolith — see `CLAUDE.md`.
 *
 * Not for form sub-pages (e.g. Send Tickets): those have no controls row and use
 * `PunchupPageShell → PunchupPageContent → PunchupPageHeader` directly.
 */
export const PunchupTablePage: FC<PunchupTablePageProps> = ({
	pageName,
	onBack,
	header,
	search,
	controlsActions,
	children,
	pageFallback,
	overlays,
}) => (
	<PunchupPageShell pageName={pageName} onBack={onBack}>
		{pageFallback ? (
			pageFallback
		) : (
			<PunchupPageContent>
				{header}
				<PunchupTableLayout>
					{(search !== undefined || controlsActions !== undefined) && (
						<PunchupPageControls search={search} actions={controlsActions} />
					)}
					{children}
				</PunchupTableLayout>
			</PunchupPageContent>
		)}
		{overlays}
	</PunchupPageShell>
);
